/**
 * PayrollSummary.jsx — Bordro Özet Raporu
 */
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, Loader2, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../api/axios';
import EmployeeAvatar from '../../../components/common/EmployeeAvatar';
import { decimalToDisplay } from '../utils/attendanceHelpers';

const PayrollSummary = ({ filters = {} }) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState({ rows: [], totals: null });
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      if (filters.department && filters.department !== 'ALL') params.department = filters.department;

      const res = await api.get('/attendance/payroll-summary', { params });
      setSummary(res.data || { rows: [], totals: null });
    } catch {
      toast.error(t('err_payroll_summary', 'Bordro özeti alınamadı.'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  const handleExport = async () => {
    try {
      const params = { format: 'csv' };
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;

      const res = await api.get('/attendance/payroll-summary/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `bordro-ozet-${filters.from_date || 'all'}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('err_export_failed', 'Dışa aktarım başarısız.'));
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">
            {t('payroll_kicker', 'Bordro Özeti')}
          </p>
          <h3 className="mt-1 text-lg font-black text-slate-900">
            {t('payroll_title', 'Bordroya Esas Puantaj Özeti')}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchSummary}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-xl bg-slate-100 px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 transition-colors"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            {t('payroll_generate', 'Oluştur')}
          </button>
          {summary.totals && (
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white hover:bg-slate-800 transition-colors"
            >
              <FileSpreadsheet size={14} />
              CSV
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 size={20} className="animate-spin text-slate-400" />
        </div>
      )}

      {!loading && !summary.totals && (
        <p className="text-sm text-slate-400 py-4">
          {t('payroll_click_generate', 'Bordro özetini görmek için "Oluştur" butonuna tıklayın.')}
        </p>
      )}

      {summary.totals && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-wider text-slate-400">
                  {t('payroll_col_employee', 'Personel')}
                </th>
                <th className="px-3 py-3 text-right text-[10px] font-black uppercase tracking-wider text-slate-400">
                  {t('payroll_col_normal', 'Normal')}
                </th>
                <th className="px-3 py-3 text-right text-[10px] font-black uppercase tracking-wider text-slate-400">
                  {t('payroll_col_overtime', 'Fazla Mesai')}
                </th>
                <th className="px-3 py-3 text-right text-[10px] font-black uppercase tracking-wider text-slate-400">
                  {t('payroll_col_absent', 'Eksik')}
                </th>
                <th className="px-3 py-3 text-right text-[10px] font-black uppercase tracking-wider text-slate-400">
                  {t('payroll_col_total', 'Toplam')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(summary.rows || []).map((row, idx) => (
                <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2.5">
                      <EmployeeAvatar firstName={row.first_name} lastName={row.last_name} size={28} />
                      <span className="text-sm font-bold text-slate-800">
                        {row.first_name} {row.last_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-slate-700">
                    {decimalToDisplay(row.normal_hours)}
                  </td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-amber-600">
                    {decimalToDisplay(row.overtime_hours)}
                  </td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-rose-600">
                    {decimalToDisplay(row.absent_hours)}
                  </td>
                  <td className="px-3 py-3 text-right text-sm font-black text-slate-900">
                    {decimalToDisplay(row.total_hours)}
                  </td>
                </tr>
              ))}
            </tbody>
            {summary.totals && (
              <tfoot>
                <tr className="bg-slate-50 font-black">
                  <td className="px-3 py-3 text-sm text-slate-800">
                    {t('payroll_total', 'GENEL TOPLAM')}
                  </td>
                  <td className="px-3 py-3 text-right text-sm text-slate-800">
                    {decimalToDisplay(summary.totals.normal_hours)}
                  </td>
                  <td className="px-3 py-3 text-right text-sm text-amber-600">
                    {decimalToDisplay(summary.totals.overtime_hours)}
                  </td>
                  <td className="px-3 py-3 text-right text-sm text-rose-600">
                    {decimalToDisplay(summary.totals.absent_hours)}
                  </td>
                  <td className="px-3 py-3 text-right text-sm text-slate-900">
                    {decimalToDisplay(summary.totals.total_hours)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default PayrollSummary;